import type { ObjectIdString } from "@Helpers/schemas/common.schema";

/**
 * The active recipient share session, readable from OUTSIDE React.
 *
 * A module-level holder rather than a React context or a Redux slice, because the consumers are not all
 * components: `ViewerImpl` is a long-lived class that fetches model geometry directly, and Redux thunks
 * receive no context. Set once by the recipient page and cleared on unmount, so exactly one share can be
 * active per tab — true by construction, since `/share/:token` is a whole-page route. Nothing in the
 * authenticated app ever sets it, so every share branch stays dormant there.
 */
export type ShareViewerSession = {
  /** The share token from the URL. */
  token: string;
  /** Present only for a password-protected link, once unlocked. */
  sessionToken?: string | undefined;
  /** Recipient's access level, so viewer chrome can gate download affordances without a round-trip. */
  canDownload: boolean;
  /**
   * Name of the item the LINK was created on (the shared folder or file), for the tab title. QA r8
   * #2: the title stays the same while the recipient moves between files inside a shared folder.
   */
  itemName?: string | undefined;
};

let activeSession: ShareViewerSession | null = null;

/**
 * modelName → geometry download ticket, for the model currently open in the shared 3D viewer.
 *
 * Populated by the recipient page BEFORE the viewer mounts, because `ViewerImpl` loads geometry from
 * inside its own readiness callback and looks each model up synchronously. The map is complete by
 * construction: `getSharedModelAssets` returns the model plus every constituent it references, which is
 * exactly the set the viewer asks for — so a lookup miss means the geometry genuinely is not shared,
 * and falling back to the authenticated URL (which would 401) is never the right answer.
 */
let geometryTicketByModelName = new Map<string, string>();

/**
 * The shared file's version history, for the recipient's version dropdown (FR-18/FR-40: the latest
 * version is only the DEFAULT — every version stays openable). Populated by the viewer page from
 * `getSharedFile` (which already returns the full list) so the header needs no fetch of its own.
 */
export type ShareViewerVersion = {
  versionId: string;
  label: string;
  isLatest: boolean;
  createdAt?: number;
};

let shareVersions: readonly ShareViewerVersion[] = [];

export function setShareViewerVersions(versions: readonly ShareViewerVersion[]): void {
  shareVersions = versions;
}

export function getShareViewerVersions(): readonly ShareViewerVersion[] {
  return shareVersions;
}

export function setShareViewerSession(session: ShareViewerSession): void {
  activeSession = session;
}

export function clearShareViewerSession(): void {
  activeSession = null;
  geometryTicketByModelName = new Map();
  shareVersions = [];
  shareTilesUrl = null;
}

/**
 * The new viewer's packed tiles of the opened model (`getSharedModelAssets` → `tiles`), as a ticketed
 * `/externalLinks/sharedViewerTiles` URL, or null: no tiles (merged model, converted before tiles), or not a
 * share view. Set by the recipient page with the geometry map; the viewer asks for a fresh one on a 403.
 */
let shareTilesUrl: string | null = null;

export function setShareViewerTiles(url: string | null): void {
  shareTilesUrl = url;
}

export function getShareViewerTiles(): string | null {
  return activeSession ? shareTilesUrl : null;
}

/** Replace the geometry map for the model about to be opened. */
export function setShareViewerGeometry(assets: readonly { modelName: string; ticket: string }[]): void {
  geometryTicketByModelName = new Map(assets.map((asset) => [asset.modelName, asset.ticket]));
}

/**
 * The geometry download ticket for one model, or null when this is not a share view.
 *
 * Read by `ViewerImpl`, which fetches the bytes through `fetchSharedGeometry` (the bare share client) in place
 * of `/fileSystemServer/getScsFile/<name>.scs`, which needs a session. Returns null outside a share so the
 * authenticated path is untouched.
 */
export function getShareGeometryTicket(modelName: string | undefined): string | null {
  if (!activeSession || !modelName) return null;
  return geometryTicketByModelName.get(modelName) ?? null;
}

/** The active session, or null in the ordinary authenticated app. */
export function getShareViewerSession(): ShareViewerSession | null {
  return activeSession;
}

/**
 * True when the current page is a recipient share view.
 *
 * Read this before taking any authenticated action inside shared viewer code: a recipient has no
 * session, so an authenticated call would 401 and (for anything going through `axiosWrapper`) could
 * bounce them to the sign-in page.
 */
export function isShareViewerSession(): boolean {
  return activeSession !== null;
}

/**
 * `fileVersionId` the recipient viewer was opened on.
 *
 * The recipient route carries the version id in the URL the same way `/designviewer/file/:fileId`
 * does, so viewer code that reads a route param keeps working unchanged.
 */
export type ShareViewerFileVersionId = ObjectIdString;
